import axios, { type AxiosRequestConfig, AxiosResponse, isAxiosError } from "axios";
import { getLoginToken } from "src/utils";
import { env } from "./env";
import { routeToUrl } from "@CommonUtils/routes";

const axiosInstance = axios.create();

axiosInstance.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (isAxiosError(error) && error.response?.status === 403) {
      console.warn("[403 Forbidden]", error.config?.url);
    }
    return Promise.reject(error);
  },
);

const enum HTTPMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DELETE",
}

const makeRequest = async <T>({
  url,
  method,
  contentType,
  xMsBlobType,
  payload,
  params,
  queryParams,
  paramsSerializer,
  withCredentials,
  signal,
  responseType,
}: {
  url: string;
  method: HTTPMethod;
  contentType?: "application/json" | "image/*" | "text/csv" | "multipart/form-data";
  xMsBlobType?: "BlockBlob";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: Record<string, any>;
  params?: Record<string, string | number>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryParams?: Record<string, any>;
  paramsSerializer?: AxiosRequestConfig["paramsSerializer"];
  withCredentials?: boolean;
  signal?: AbortSignal;
  responseType?: AxiosRequestConfig["responseType"];
}): Promise<AxiosResponse<T>> => {
  const headers = {
    "Content-Type": contentType || "application/json",
    ...(xMsBlobType ? { "x-ms-blob-type": "BlockBlob" } : {}),
    ...(getLoginToken() &&
    !(method === HTTPMethod.PUT && contentType === "text/csv" && xMsBlobType === "BlockBlob")
      ? { Authorization: `Bearer ${getLoginToken()}` }
      : {}),
  };

  return axiosInstance<T>({
    url: params ? routeToUrl(url, params) : url,
    method,
    data: payload,
    headers,
    params: queryParams,
    paramsSerializer,
    withCredentials,
    signal,
    responseType,
  });
};

export const getRequest = <T>(
  url: string,
  data?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryParams?: Record<string, any>;
    params?: Record<string, string | number>;
    paramsSerializer?: AxiosRequestConfig["paramsSerializer"];
    withCredentials?: boolean;
    signal?: AbortSignal;
    responseType?: AxiosRequestConfig["responseType"];
  },
) => {
  return makeRequest<T>({
    url,
    method: HTTPMethod.GET,
    params: data?.params,
    queryParams: data?.queryParams,
    paramsSerializer: data?.paramsSerializer,
    withCredentials: data?.withCredentials,
    signal: data?.signal,
    responseType: data?.responseType,
  });
};

export const postRequest = <T>(
  url: string,
  data?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload?: Record<string, any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryParams?: Record<string, any>;
    params?: Record<string, string | number>;
    paramsSerializer?: AxiosRequestConfig["paramsSerializer"];
  },
) => {
  return makeRequest<T>({
    url,
    method: HTTPMethod.POST,
    params: data?.params,
    payload: data?.payload,
    queryParams: data?.queryParams,
    paramsSerializer: data?.paramsSerializer,
  });
};

export const putRequest = <T>(
  url: string,
  data: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload: any;
    params?: Record<string, string | number>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryParams?: Record<string, any>;
    paramsSerializer?: AxiosRequestConfig["paramsSerializer"];
    contentType?: "application/json" | "image/*" | "text/csv";
    xMsBlobType?: "BlockBlob";
  },
) =>
  makeRequest<T>({
    url,
    method: HTTPMethod.PUT,
    params: data.params,
    queryParams: data.queryParams,
    paramsSerializer: data.paramsSerializer,
    contentType: data.contentType,
    xMsBlobType: data.xMsBlobType,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    payload: data.payload,
  });

export const deleteRequest = <T>(
  url: string,
  data?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload?: Record<string, any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryParams?: Record<string, any>;
    params?: Record<string, string | number>;
    paramsSerializer?: AxiosRequestConfig["paramsSerializer"];
  },
) =>
  makeRequest<T>({
    url,
    method: HTTPMethod.DELETE,
    payload: data?.payload,
    params: data?.params,
    queryParams: data?.queryParams,
    paramsSerializer: data?.paramsSerializer,
  });

export const postFormDataRequest = <T>(
  url: string,
  data: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryParams?: Record<string, any>;
    params?: Record<string, string | number>;
    paramsSerializer?: AxiosRequestConfig["paramsSerializer"];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload: Record<string, any>;
  },
) => {
  const formData = new FormData();

  for (const [key, value] of Object.entries(data.payload)) {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        formData.append(key, item);
      });
    } else {
      formData.append(key, value);
    }
  }

  return makeRequest<T>({
    url,
    method: HTTPMethod.POST,
    params: data.params,
    queryParams: data.queryParams,
    paramsSerializer: data.paramsSerializer,
    payload: formData,
    contentType: "multipart/form-data",
  });
};

export const API = env.VITE_API_URL;
